
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { nanoid } from 'nanoid';

interface ChatSession {
  id: string;
  session_id: string;
  session_name: string;
  created_at: string;
  last_message_at: string | null;
  message_count: number;
  organisation_id: string | null;
}

export const useChatSessions = (organisationId?: string) => {
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();

  const loadSessions = useCallback(async () => {
    try {
      setIsLoading(true);
      const { data: { user } } = await supabase.auth.getUser();

      if (!user) {
        setSessions([]);
        return;
      }

      let query = supabase
        .from('chat_sessions')
        .select('*')
        .eq('user_id', user.id)
        .order('last_message_at', { ascending: false, nullsFirst: false });

      if (organisationId) {
        query = query.eq('organisation_id', organisationId);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error loading sessions:', error);
        throw error;
      }

      setSessions(data || []);
    } catch (error: any) {
      console.error('Error loading chat sessions:', error);
      toast({
        title: 'Error',
        description: 'Failed to load chat sessions',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  }, [organisationId, toast]);

  const createSession = useCallback(async (sessionName: string, orgId?: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();

      if (!user) {
        throw new Error('User not authenticated');
      }

      const sessionId = orgId
        ? `org_${orgId}_user_${user.id}_${Date.now()}`
        : `user_${user.id}_${nanoid()}`;

      const { data, error } = await supabase
        .from('chat_sessions')
        .insert({
          session_id: sessionId,
          session_name: sessionName,
          user_id: user.id,
          organisation_id: orgId || null,
          expires_at: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(), // 24 hours
          openai_session_token: nanoid()
        })
        .select()
        .single();

      if (error) throw error;

      await loadSessions();
      return data.session_id;
    } catch (error: any) {
      console.error('Error creating session:', error);
      toast({
        title: 'Error',
        description: 'Failed to create new session',
        variant: 'destructive',
      });
      return null;
    }
  }, [loadSessions, toast]);

  const renameSession = useCallback(async (sessionId: string, newName: string) => {
    try {
      const { error } = await supabase
        .from('chat_sessions')
        .update({ session_name: newName })
        .eq('session_id', sessionId);

      if (error) throw error;

      await loadSessions();
      toast({
        title: 'Success',
        description: 'Session renamed successfully',
      });
    } catch (error: any) {
      console.error('Error renaming session:', error);
      toast({
        title: 'Error',
        description: 'Failed to rename session',
        variant: 'destructive',
      });
    }
  }, [loadSessions, toast]);

  const deleteSession = useCallback(async (sessionId: string) => {
    try {
      // First, get all messages in this session to find export IDs
      const { data: messages, error: messagesError } = await supabase
        .from('chat_messages')
        .select('tool_calls')
        .eq('session_id', sessionId)
        .not('tool_calls', 'is', null);

      if (messagesError) {
        console.error('Error fetching messages for export cleanup:', messagesError);
      }

      // Extract export IDs from message tool_calls
      const exportIds: string[] = [];
      if (messages) {
        for (const message of messages) {
          try {
            const toolCalls = message.tool_calls;
            if (toolCalls && typeof toolCalls === 'object' && !Array.isArray(toolCalls)) {
              // Check if exportInfo exists in tool_calls
              const toolCallsObj = toolCalls as { [key: string]: any };
              if (toolCallsObj.exportInfo && toolCallsObj.exportInfo.export_id) {
                exportIds.push(String(toolCallsObj.exportInfo.export_id));
              }
            }
          } catch (parseError) {
            console.warn('Failed to parse tool_calls for export cleanup:', parseError);
          }
        }
      }

      // Delete export files from storage and database if any exports found
      if (exportIds.length > 0) {
        console.log(`Found ${exportIds.length} exports to clean up for session ${sessionId}:`, exportIds);

        // Get export file paths from csv_exports table
        const { data: exports, error: exportsError } = await supabase
          .from('csv_exports')
          .select('export_id, file_path, chunk_files')
          .in('export_id', exportIds);

        if (exportsError) {
          console.error('Error fetching export files for cleanup:', exportsError);
        } else if (exports) {
          // Collect all file paths to delete from storage
          const filesToDelete: string[] = [];

          for (const exportRecord of exports) {
            // Add main export file if it exists
            if (exportRecord.file_path) {
              filesToDelete.push(exportRecord.file_path);
            }

            // Add chunk files if they exist
            if (exportRecord.chunk_files) {
              try {
                const chunkFiles = JSON.parse(String(exportRecord.chunk_files));
                if (Array.isArray(chunkFiles)) {
                  filesToDelete.push(...chunkFiles);
                }
              } catch (parseError) {
                console.warn('Failed to parse chunk_files for export:', exportRecord.export_id);
              }
            }
          }

          // Delete files from storage
          if (filesToDelete.length > 0) {
            const { error: storageError } = await supabase.storage
              .from('company-files')
              .remove(filesToDelete);

            if (storageError) {
              console.error('Error deleting export files from storage:', storageError);
            } else {
              console.log(`Deleted ${filesToDelete.length} export files from storage`);
            }
          }
        }

        // Delete export records from database
        const { error: exportDeleteError } = await supabase
          .from('csv_exports')
          .delete()
          .in('export_id', exportIds);

        if (exportDeleteError) {
          console.error('Error deleting export records:', exportDeleteError);
        } else {
          console.log(`Deleted ${exportIds.length} export records`);
        }

        // Delete export chunks
        const { error: chunksDeleteError } = await supabase
          .from('csv_export_chunks')
          .delete()
          .in('export_id', exportIds);

        if (chunksDeleteError) {
          console.error('Error deleting export chunks:', chunksDeleteError);
        } else {
          console.log(`Deleted export chunks for ${exportIds.length} exports`);
        }
      }

      // Delete messages
      const { error: deleteMessagesError } = await supabase
        .from('chat_messages')
        .delete()
        .eq('session_id', sessionId);

      if (deleteMessagesError) throw deleteMessagesError;

      // Finally delete the session
      const { error: sessionError } = await supabase
        .from('chat_sessions')
        .delete()
        .eq('session_id', sessionId);

      if (sessionError) throw sessionError;

      await loadSessions();
      toast({
        title: 'Success',
        description: 'Session and associated exports deleted successfully',
      });
    } catch (error: any) {
      console.error('Error deleting session:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete session',
        variant: 'destructive',
      });
    }
  }, [loadSessions, toast]);

  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  return {
    sessions,
    isLoading,
    createSession,
    renameSession,
    deleteSession,
    refreshSessions: loadSessions
  };
};
